<template>
  <div>
    <v-menu
      auto
      offset-y
      :z-index="200"
      :content-class="$style.notificationTextWrapper"
      :width="hasBlockingreasons ? 320 : undefined"
      :close-on-content-click="false"
    >
      <template #activator="{ on }">
        <div v-if="!hasBlockingreasons" :class="$style.iconWidth"></div>
        <div v-on="on">
          <v-icon v-if="hasBlockingreasons" :class="[$style.notify, $style.iconWidth]"
            >mdi-alert-circle-outline</v-icon
          >
        </div>
      </template>

      <v-list dense flat tile>
        <v-list-item-group>
          <v-list-item :class="$style.listItem" :ripple="false" @click="navigateToInformationTab">
            <v-list-item-content :class="$style.listContent">
              <h2 class="mb-1 h4">
                {{ $t('account.blockingreasons.notificationIconText') }}
              </h2>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-for="blockingreason in blockingReasons"
            :key="blockingreason.key"
            :class="$style.listItem"
            :ripple="false"
            ><section :class="$style.blockingreason">
              <h4 :class="$style.brandLogo" class="mt-2 mb-1">
                <BrandLogoRef
                  :brand="blockingreason.company"
                  align="left"
                  :class="$style.logo"
                  max-height="2rem"
                  max-width="11rem"
                />
                <span class="visually-hidden">{{ blockingreason.company }}</span>
              </h4>
              <v-expansion-panels flat accordion :class="$style.panel">
                <v-expansion-panel v-for="reason in blockingreason.blockingReasons" :key="reason">
                  <v-expansion-panel-header
                    ><b>{{
                      $t(`account.blockingreasons.shortReasons.${reason}`)
                    }}</b></v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <RawHtml
                      :template="`
                        <div>
                          ${getBlockingreasonTranslation(blockingreason, reason)}
                        </div>
                      `"
                    >
                    </RawHtml>
                    <template v-if="reason === 'VR'">
                      <div :class="$style.blockingreasonConfirmButton">
                        <ConfirmBackorderEditButton :context-brand="blockingreason.salesOrg" />
                      </div>
                    </template>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </section>
          </v-list-item>
          <v-list-item :class="$style.listItem" :ripple="false" @click="navigateToInformationTab">
            <v-list-item-content :class="$style.listContent">
              <div :class="$style.actions">
                <v-btn
                  dark
                  v-bind="$attrs"
                  elevation="0"
                  color="secondary"
                  :class="['btn action-button action-button--buy', $style.button]"
                  @click="navigateToInformationTab"
                >
                  {{ $t('account.blockingreasons.more_information') }}
                </v-btn>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-menu>
  </div>
</template>
<script>
import { get, flatten, compact, uniq } from 'lodash-es'
import { mapGetters } from 'vuex'
import { COMPANY_CODE_TO_BRAND } from '@/js/helper/dp-usermanagement-constants'

import { types as STOREMODULES } from '@/js/plugins/store/store-modules.js'
import AIMEOS_GETTERS from '@/js/plugins/store/modules/aimeos/getter-types/index.js'
import {
  CONSTANTS as MESSAGE_CENTER_CONSTANTS,
  FILTER_TYPES,
} from '@/js/helper/message-center-constants.js'
import RawHtml from '@/js/components/Shared/RawHtml'
import BrandLogoRef from '@/js/components/Shared/BrandLogoRef.vue'
import ConfirmBackorderEditButton from '@/js/components/Account/Backorders/components/shared/ConfirmBackorderEditButton.vue'
import MessageDataBlockingreasonMixin from '@/js/mixins/message-data-blockingreason-mixin.js'

export default {
  name: 'AccountBlockNotification',
  components: {
    RawHtml,
    BrandLogoRef,
    ConfirmBackorderEditButton,
  },
  mixins: [MessageDataBlockingreasonMixin],
  computed: {
    ...mapGetters(STOREMODULES.AIMEOS, {
      blockingreasonData: AIMEOS_GETTERS.BLOCKINGREASONS_DATA,
      blockingreasonObjects: AIMEOS_GETTERS.BLOCKINGREASONS_LIST,
    }),
    hasBlockingreasons() {
      const reasons = uniq(
        compact(
          flatten(
            this.blockingreasonObjects.map(obj => {
              return obj.blockingReasons
            }) ?? [],
          ),
        ),
      )

      // Show warning if there are blockingreasons AND there is no update queued
      const updateIsQueued = get(this.blockingreasonData, 'update_queued', 1)
      return !!reasons.length && !updateIsQueued
    },
    blockingReasons() {
      return this.blockingreasonObjects.map(i => {
        return {
          ...i,
          company: COMPANY_CODE_TO_BRAND[i.salesOrg],
          key: `${i.salesOrg}-${i.blockingReasons.join('-')}`,
        }
      })
    },
  },
  methods: {
    navigateToInformationTab() {
      const query = { tab: 'info', filter: FILTER_TYPES.MESSAGING_ALL }

      this.$router
        .push({
          name: MESSAGE_CENTER_CONSTANTS.ROUTES.LIST,
          query,
        })
        // Catch to avoid "NavigationDuplicated" error from vue-router when navigating inside message center
        .catch(() => {})
    },
    getBlockingreasonTranslation(data, reason) {
      if (['VR'].includes(reason)) {
        const formattedDate = data.salesOrgDeliveryBlockDate
          ? this.formatDate(data.salesOrgDeliveryBlockDate)
          : ''

        // Custom translation with additional value for translation
        return this.$t(`account.blockingreasons.reasons.${reason}`, {
          additionalValue: formattedDate,
        })
      } else {
        return this.$t(`account.blockingreasons.reasons.${reason}`)
      }
    },
  },
}
</script>
<style lang="scss" module>
.iconWidth {
  width: 3rem;
}

.notificationTextWrapper {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    transform: translateX(calc(-50% + 2.5rem));
  }
  max-height: 100% !important;
}

.notify {
  color: var(--red) !important;
  font-size: 2.5rem !important;

  cursor: pointer;
}

.messageCenterLink {
  color: var(--primary);
  text-decoration: underline;

  cursor: pointer;
}

.actions {
  margin-top: 0.5rem;

  :global(.v-btn) {
    width: 100%;

    font-size: 1rem;
  }
}

.blockingreason {
  width: 20rem;
  max-width: 20rem;
}

.blockingreasonConfirmButton {
  margin: 0.5rem 0;
}

.panel {
  :global(.v-expansion-panel-header),
  :global(.v-expansion-panel-content__wrap) {
    padding-right: 0;
    padding-left: 0;
  }
}

.brandLogo {
  display: flex;
  justify-content: flex-start;

  .logo {
    width: 100%;
  }
}
</style>
